// Pure FE helpers for My Trips. deriveTripStatus mirrors the backend pure
// function in backend/services/trips/tripRules.js — keep the two in sync.
// The FE is the source of truth for status (it knows the user's local date).

import { TripStatus, TripStage, TripDisplayStatus } from './types';
import { isNightTrainMode } from '../../shared/transport';

/** Normalize a date-ish value to a day ordinal (local), or null. */
function toDayNumber(value: string | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Use the date's own Y/M/D so a date-only ISO ("2026-06-09") and a local
  // "today" compare on the same calendar footing.
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000);
}

function todayDayNumber(today: Date): number {
  return Math.floor(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) / 86400000);
}

/**
 * Derive a trip's status from its date range (Rule 6). No dates => future
 * (idea). A missing bound is treated as open-ended.
 */
export function deriveTripStatus(
  dateStart: string | null,
  dateEnd: string | null,
  today: Date = new Date(),
): TripStatus {
  const start = toDayNumber(dateStart);
  const end = toDayNumber(dateEnd);
  if (start === null && end === null) return 'future';
  const now = todayDayNumber(today);
  if (end !== null && end < now) return 'past';
  if (start !== null && start > now) return 'future';
  return 'now';
}

/**
 * Merge the date-derived temporal status with the user-set stage into the
 * single value the StatusPill shows. Temporal facts win: a trip currently
 * within its dates is "now", a finished one is "past"; otherwise (upcoming or
 * undated) we show the user's stage (idea/organizing/booked).
 */
export function resolveDisplayStatus(
  dateStart: string | null,
  dateEnd: string | null,
  stage: TripStage,
  today: Date = new Date(),
): TripDisplayStatus {
  const temporal = deriveTripStatus(dateStart, dateEnd, today);
  if (temporal === 'now') return 'now';
  if (temporal === 'past') return 'past';
  return stage; // 'future' defers to the user's stage
}

/**
 * The user-entered override for a leg's CURRENT transport mode (user-editable
 * costs & times — schema doc `userOverrides`). Only the selected mode's entry
 * counts toward the timeline, pills, and totals; other modes' entries sleep
 * until that mode is chosen. Mirrors backend pricingService.currentModeOverride.
 */
export function currentModeOverride(leg: {
  transportMode?: string | null;
  userOverrides?: Record<string, { costCents?: number; durationMinutes?: number }> | null;
}): { costCents?: number; durationMinutes?: number } | null {
  const mode = leg.transportMode || 'train';
  const ov = leg.userOverrides && typeof leg.userOverrides === 'object' ? leg.userOverrides[mode] : null;
  return ov && typeof ov === 'object' ? ov : null;
}

/** Parse a user-typed euro amount ("39", "39.50", "€ 39,50") → cents, or null. */
export function parseEuroInput(raw: string): number | null {
  const s = String(raw || '').replace(/€/g, '').replace(/\s/g, '').replace(',', '.');
  if (!s || !/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const cents = Math.round(parseFloat(s) * 100);
  return Number.isFinite(cents) ? cents : null;
}

/** Parse a user-typed duration ("5:30", "5h30", "5h", "90m", "5", "5.5") → minutes, or null. */
export function parseDurationInput(raw: string): number | null {
  const s = String(raw || '').trim().toLowerCase().replace(/\s/g, '');
  if (!s) return null;
  let m = s.match(/^(\d{1,3}):([0-5]?\d)$/) || s.match(/^(\d{1,3})h([0-5]?\d)$/);
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  m = s.match(/^(\d{1,3})h$/);
  if (m) return parseInt(m[1], 10) * 60;
  m = s.match(/^(\d{1,5})(m|min)$/);
  if (m) return parseInt(m[1], 10);
  m = s.match(/^(\d{1,3}(?:[.,]\d{1,2})?)$/);
  if (m) return Math.round(parseFloat(m[1].replace(',', '.')) * 60);
  return null;
}

/** Exact h:mm for user-entered durations (no 10-min rounding — it's their number). */
export function formatDurationExact(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}:${String(m).padStart(2, '0')}h`;
}

/** Exact euro string for user-entered costs (keeps cents only when present). */
export function formatEuroExact(cents: number): string {
  return cents % 100 === 0 ? `€${cents / 100}` : `€${(cents / 100).toFixed(2)}`;
}

// Canonical string form of a userOverrides map for dirty-checking: value
// fields only (editedAt is server-stamped), empty entries dropped, keys sorted.
export function overridesKey(o: Record<string, { costCents?: number; durationMinutes?: number }> | null | undefined): string {
  const out: Record<string, { costCents?: number; durationMinutes?: number }> = {};
  for (const k of Object.keys(o || {}).sort()) {
    const e = (o || {})[k] || {};
    const entry: { costCents?: number; durationMinutes?: number } = {};
    if (e.costCents != null) entry.costCents = e.costCents;
    if (e.durationMinutes != null) entry.durationMinutes = e.durationMinutes;
    if (entry.costCents != null || entry.durationMinutes != null) out[k] = entry;
  }
  return JSON.stringify(out);
}

/** Coerce a possibly-Decimal (string) value to a finite number, or fallback. */
export function coerceDecimal(value: string | number | null | undefined, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : fallback;
}

// Approximation rounding for My Trips summaries: times and costs are shown as
// rounded-up estimates (the safe direction). See the ApproxNote footnote.
export function ceilHours(hours: number): number {
  return Math.ceil(hours);
}

/**
 * Formats an ESTIMATED leg duration (in minutes) rounded UP to the next
 * quarter hour, e.g. 218 → "3:45h", 25 → "30 min", 180 → "3h".
 *
 * 15-minute steps on purpose (Paulo, 2026-08-15): our ground times are a
 * routed driving proxy × a coarse per-mode factor and flights carry a flat
 * 2 h 30 overhead — 10-minute granularity would read like timetable precision
 * we can't back. Rounding UP is the safe direction for planning. The DB
 * keeps the exact minutes (JourneyLeg.durationMinutes); this is display
 * only. User-ENTERED times never pass through here — formatDurationExact.
 */
export function formatDurationMin(minutes: number): string {
  const totalMin = Math.ceil(minutes / 15) * 15;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}:${String(m).padStart(2, '0')}h`;
}

/** Round a euro amount UP to the next multiple of 10. */
export function ceilEuros(euros: number): number {
  return Math.ceil(euros / 10) * 10;
}

/** Convenience: cents (string|number) → euros rounded up to the next €10. */
export function ceilEurosFromCents(cents: string | number | null | undefined): number {
  return ceilEuros(coerceDecimal(cents, 0) / 100);
}

/**
 * Compact price-range label from cent bounds: "€130–150" (low floored, high
 * ceiled to €10s — same convention as the journey detail's InfoPills), or a
 * single "€140" when both round to the same value.
 */
export function euroRangeLabel(lowCents: string | number, highCents: string | number): string {
  const lowEur = Math.floor(coerceDecimal(lowCents, 0) / 100 / 10) * 10;
  const highEur = Math.ceil(coerceDecimal(highCents, 0) / 100 / 10) * 10;
  return lowEur === highEur ? `€${highEur}` : `€${lowEur}–${highEur}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtMonthYear(d: Date): string {
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Build the human date label for the MetaLine, covering the design-handoff.md's cases:
 * single month, multi-month/year range, open-ended ("Dates not set").
 */
/** The brief's rough length, as the trip form offers it (TripParams.durationPreset). */
export const DURATION_PRESET_LABEL: Record<string, string> = {
  weekend: 'A weekend',
  one_week: 'About 1 week',
  two_weeks: 'About 2 weeks',
  one_month: 'About a month',
  flexible: 'Flexible dates',
};

/**
 * "Oct 2026", "Jul – Aug 2026"; without dates, the brief's rough length when
 * the traveller gave one ("About 2 weeks"), since "Dates not set" read as if
 * they had said nothing (Paulo 2026-09-30); "Dates not set" only without
 * either.
 */
export function formatTripDateRange(dateStart: string | null, dateEnd: string | null, durationPreset?: string | null): string {
  const start = dateStart ? new Date(dateStart) : null;
  const end = dateEnd ? new Date(dateEnd) : null;
  if (!start && !end) return (durationPreset && DURATION_PRESET_LABEL[durationPreset]) || 'Dates not set';
  if (start && !end) return `From ${fmtMonthYear(start)}`;
  if (!start && end) return `Until ${fmtMonthYear(end)}`;
  // both present
  const s = start as Date;
  const e = end as Date;
  const sameMonth = s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth();
  if (sameMonth) return fmtMonthYear(s);
  return `${fmtMonthYear(s)} – ${fmtMonthYear(e)}`;
}

/** Inclusive whole-day count between two dates, or null when open-ended. */
export function tripDurationDays(dateStart: string | null, dateEnd: string | null): number | null {
  if (!dateStart || !dateEnd) return null;
  const s = toDayNumber(dateStart);
  const e = toDayNumber(dateEnd);
  if (s === null || e === null) return null;
  return Math.max(0, e - s) + 1;
}

// ---- Expected travel schedule (journey timeline + leg sheet) ----------------
// One pure O(n) walk over ordered stops + legs: from the journey's departure
// date, arrival_i = departure_(i-1) + leg travel time, departure_i = arrival_i
// + nights at the stop. A leg with a USER-SET ticket date (ticketDetails.date)
// re-anchors the walk: that leg runs on the user's date and everything
// downstream follows from it. Day precision, all math in UTC ms. Cheap enough
// to rerun on every nights-stepper tick.

const DAY_MS = 86400000;

/** Nights derived from a stop's stored duration hours (mirrors NightsStepper). */
export function hoursToNights(hours?: number | string | null): number {
  const h = coerceDecimal(hours, 0);
  return h ? Math.max(0, Math.round(h / 24)) : 0;
}

/**
 * A place's name without its country: some stops carry it ("Berlin,
 * Germany"). Titles show the country as a flag instead (Paulo 2026-09-28).
 */
export function cityOnly(name?: string | null): string {
  return String(name || '').split(',')[0].trim();
}

/**
 * Changes (night trains spec §7.4): a change is a stop with exactly 0 nights
 * that is neither the first nor the last stop of the journey. Nights that
 * were never set (null) are not 0 nights. On screen a change is a stop like
 * any other, with its stepper at 0 (Paulo 2026-09-28, instead of the spec's
 * "Change here" card); only the night-train sheet names it ("Change in …").
 * `nightsOverlay` carries live stepper edits.
 * Backend twin: backend/services/nightTrains/changes.js. Keep the two in step.
 * @returns the ids of the stops that are changes
 */
export function changeStopIds(
  stops: Array<{ id?: string; order?: number; stopDurationHours?: number | string | null }>,
  nightsOverlay?: Record<string, number>,
): Set<string> {
  const ordered = [...stops].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out = new Set<string>();
  ordered.forEach((s, i) => {
    if (!s.id || i === 0 || i === ordered.length - 1) return;
    const live = nightsOverlay ? nightsOverlay[s.id] : undefined;
    const zero = live != null ? live === 0 : (s.stopDurationHours != null && Number(s.stopDurationHours) === 0);
    if (zero) out.add(s.id);
  });
  return out;
}

/**
 * How each stop is labelled in the journey's sequence, on the timeline
 * (RouteTimeline) and the route map (JourneyRouteMap's round-trip numbers)
 * alike, Paulo 2026-10-03: the first stop is the Start and is not counted; on
 * a round trip the last stop, back where it started, is the End; every other
 * stop is numbered from 1 (a one-way trip's destination included). Display
 * only: stops are identified by id everywhere else (replace, remove, the
 * sheets, the map pick).
 * @param stops in journey order
 */
export type StopSequenceLabel = { kind: 'start' } | { kind: 'end' } | { kind: 'stop'; number: number };

interface SequenceStop {
  latitude?: number | string | null;
  longitude?: number | string | null;
  city?: string | null;
  location?: string | null;
  country?: string | null;
}

// Same place: the same coordinates (the map's round-trip rule), or, without
// coordinates on both, the same city in the same country. Backend twin:
// placeKey in backend/services/trips/tripRules.js (the route summary counts
// distinct places with it); keep the two in step.
function samePlace(a: SequenceStop, b: SequenceStop): boolean {
  const coords = (s: SequenceStop) => {
    const lat = s.latitude == null ? NaN : Number(s.latitude);
    const lng = s.longitude == null ? NaN : Number(s.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) ? [lat, lng] : null;
  };
  const ca = coords(a);
  const cb = coords(b);
  if (ca && cb) return ca[0] === cb[0] && ca[1] === cb[1];
  const name = (s: SequenceStop) => cityOnly(s.city || s.location).trim().toLowerCase();
  return !!name(a) && name(a) === name(b) && (a.country || '') === (b.country || '');
}

export function stopSequenceLabels(stops: SequenceStop[]): StopSequenceLabel[] {
  const n = stops.length;
  const roundTrip = n > 2 && samePlace(stops[0], stops[n - 1]);
  let count = 0;
  return stops.map((_, i): StopSequenceLabel => {
    if (i === 0) return { kind: 'start' };
    if (i === n - 1 && roundTrip) return { kind: 'end' };
    count += 1;
    return { kind: 'stop', number: count };
  });
}

/**
 * Accommodation (stay) cost rollup — user-entered prices only, so displays
 * keep them UNSTARRED (the star marks estimates; these numbers are the
 * user's own).
 */
export interface StayCostSummary {
  /** Sum of entered stay prices; null when nothing is priced yet. */
  sumCents: number | null;
  staysPriced: number;
  staysTotal: number;
}

export function stayCostStateOf(s: StayCostSummary): 'complete' | 'partial' | 'none' {
  if (s.sumCents == null) return 'none';
  return s.staysTotal > 0 && s.staysPriced >= s.staysTotal ? 'complete' : 'partial';
}

/**
 * Client-side rollup for surfaces fed raw stops (journey detail pills).
 * Completeness counts EVERY stop (Paulo 2026-09-12, supersedes the
 * 2026-08-18 overnight-only rule): the pill headlines "N stays", so a sum
 * missing any of those N renders as "€X+". Mirrors backend
 * tripRules.buildAlternativeSummary.
 */
export function computeStayCosts(stops: Array<{ stayDetails?: { costCents?: number | null } | null }>): StayCostSummary {
  let sum = 0;
  let any = false;
  let staysPriced = 0;
  for (const s of stops) {
    const cost = s.stayDetails?.costCents;
    if (cost != null) {
      staysPriced += 1;
      sum += Number(cost);
      any = true;
    }
  }
  return { sumCents: any ? sum : null, staysPriced, staysTotal: stops.length };
}

export interface ScheduleStop {
  id?: string;
  order?: number;
  stopDurationHours?: number | string | null;
}
export interface ScheduleLeg {
  id: string;
  fromStopId?: string;
  toStopId?: string;
  transportMode?: string | null;
  durationMinutes?: number | string | null;
  durationHours?: number | string | null;
  userOverrides?: Record<string, { costCents?: number; durationMinutes?: number }> | null;
  ticketDetails?: { date?: string | null } | null;
  /** Night-train legs: the ride (see legArrivalMs). */
  nightTrain?: { boarding?: { time?: string }; durationMin?: number; nightsOnBoard?: number } | null;
}

/** Nights a leg spends on board: a night train's, 0 for every other leg. */
export function legNightsOnBoard(l: { transportMode?: string | null; nightTrain?: { nightsOnBoard?: number } | null }): number {
  if (!isNightTrainMode(l.transportMode) || !l.nightTrain) return 0;
  return Math.max(1, Number(l.nightTrain.nightsOnBoard) || 1);
}

/**
 * When the traveller reaches the next stop, leaving on `leaveMs`. A night
 * train boards in the evening of that day and gets off `durationMin` later,
 * so the next stop starts on a later day. Twin of the backend's
 * services/nightTrains/schedule.js: keep the two in step.
 */
function legArrivalMs(leaveMs: number, leg?: ScheduleLeg): number {
  if (!leg) return leaveMs;
  const board = /^(\d{1,2}):(\d{2})$/.exec(leg.nightTrain?.boarding?.time || '');
  if (isNightTrainMode(leg.transportMode) && board) {
    const ride = currentModeOverride(leg)?.durationMinutes ?? leg.nightTrain?.durationMin;
    if (ride != null && Number(ride) > 0) {
      const dayStart = Math.floor(leaveMs / DAY_MS) * DAY_MS;
      return dayStart + (Number(board[1]) * 60 + Number(board[2]) + Number(ride)) * 60000;
    }
  }
  return leaveMs + legTravelMinutes(leg) * 60000;
}

/** A leg's travel minutes, preferring the current mode's user override. */
export function legTravelMinutes(l: ScheduleLeg): number {
  const ovr = currentModeOverride(l);
  if (ovr?.durationMinutes != null) return Number(ovr.durationMinutes);
  const m = coerceDecimal(l.durationMinutes, NaN);
  if (Number.isFinite(m)) return m;
  const h = coerceDecimal(l.durationHours, NaN);
  return Number.isFinite(h) ? h * 60 : 0;
}

export interface JourneySchedule {
  /** Per stop id: expected arrival/departure (UTC ms, day precision).
   *  `conflict` = the departure lands before the arrival (a user-set leg date
   *  earlier than the route before it). */
  stopWindows: Record<string, { fromMs: number; toMs: number; conflict?: boolean }>;
  /** Per leg id: the day the transport runs (UTC ms); `conflict` as above. */
  legDays: Record<string, { ms: number; conflict?: boolean }>;
}

export function computeJourneySchedule(
  stops: ScheduleStop[],
  legs: ScheduleLeg[],
  departureDate?: string | null,
  /** Live stepper edits overlaying the stored nights (RouteTimeline). */
  nightsOverlay?: Record<string, number>,
): JourneySchedule {
  const out: JourneySchedule = { stopWindows: {}, legDays: {} };
  const ordered = [...stops].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const orderedLegs = [...legs];
  const legBetween = (fromId?: string, toId?: string) =>
    orderedLegs.find((l) => l.fromStopId === fromId && l.toStopId === toId);
  const nightsFor = (s: ScheduleStop) =>
    s.id && nightsOverlay && nightsOverlay[s.id] != null ? nightsOverlay[s.id] : hoursToNights(s.stopDurationHours);

  // Without a journey departure date the cursor starts unknown (NaN) — but a
  // user-set ticket date is an anchor in its own right: its leg shows the date
  // and everything downstream is computed from it.
  let cursor = departureDate ? Date.parse(departureDate) : NaN;
  for (let i = 0; i < ordered.length; i++) {
    const s = ordered[i];
    const arrive = cursor;
    const nights = nightsFor(s);
    let leave = Number.isFinite(arrive) ? arrive + nights * DAY_MS : NaN;
    const next = ordered[i + 1];
    const leg = next ? legBetween(s.id, next.id) : undefined;
    // A user-set ticket date re-anchors: the leg runs on THAT day. Never block
    // the user, but flag inconsistencies (red in the UI):
    // - the STOP whenever the stay its dates imply disagrees with the nights
    //   stepper (this includes time running backwards, implied < 0);
    // - the LEG only when its date lands BEFORE the arrival (backwards).
    const explicit = leg?.ticketDetails?.date ? Date.parse(leg.ticketDetails.date) : NaN;
    let stopConflict = false;
    let legConflict = false;
    if (Number.isFinite(explicit)) {
      if (Number.isFinite(arrive)) {
        const impliedNights = Math.round((explicit - arrive) / DAY_MS);
        stopConflict = impliedNights !== nights;
        legConflict = explicit < arrive;
      }
      leave = explicit;
    }
    if (s.id && Number.isFinite(arrive) && Number.isFinite(leave)) {
      out.stopWindows[s.id] = { fromMs: arrive, toMs: leave, ...(stopConflict ? { conflict: true } : {}) };
    }
    if (!next) break;
    if (leg && Number.isFinite(leave)) out.legDays[leg.id] = { ms: leave, ...(legConflict ? { conflict: true } : {}) };
    cursor = Number.isFinite(leave) ? legArrivalMs(leave, leg) : NaN;
  }
  return out;
}

/** UTC ms → "YYYY-MM-DD" (native date-input value format). */
export function msToIsoDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}
